import React, { useState, useEffect, useCallback } from 'react';
import BannerCarousel from '../components/BannerCarousel';
import ProductsSection from '../components/ProductsSection';
import AboutSection from '../components/AboutSection';
import ContactSection from '../components/ContactSection';
import SEO from '../components/common/SEO';
import { productService } from '../services/productService';
import { collectionService } from '../services/collectionService';
import { Product, Collection } from '../types';

const PRODUCTS_PER_PAGE = 12;

const HomePage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [totalProducts, setTotalProducts] = useState<number>(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productsResponse, collectionsData] = await Promise.all([
        productService.getPaginated(1, PRODUCTS_PER_PAGE),
        collectionService.getAll(),
      ]);
      setProducts(productsResponse.data);
      setCurrentPage(productsResponse.current_page);
      setHasMore(productsResponse.current_page < productsResponse.last_page);
      setTotalProducts(productsResponse.total);
      setCollections(collectionsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    try {
      setLoadingMore(true);
      const nextPage = currentPage + 1;
      const response = await productService.getPaginated(nextPage, PRODUCTS_PER_PAGE);
      setProducts(prev => [...prev, ...response.data]);
      setCurrentPage(response.current_page);
      setHasMore(response.current_page < response.last_page);
    } catch (error) {
      console.error('Error loading more products:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [currentPage, hasMore, loadingMore]);

  return (
    <>
      <SEO
        title="Inicio"
        description="Descubre las últimas tendencias en moda. Compra ropa única y de calidad en Topped. Envíos a toda Colombia."
        keywords="ropa, moda, tienda online, comprar ropa, fashion, tendencias, Colombia"
        type="website"
      />
      <BannerCarousel />
      <ProductsSection
        products={products}
        collections={collections}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        totalProducts={totalProducts}
        onLoadMore={loadMore}
      />
      <AboutSection />
      <ContactSection />
    </>
  );
};

export default HomePage;
